import React from 'react';
import { ImagesSection_MainQuery, MediaRole, MediaType } from '../../../../codegen/graphql';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdClose, MdPlayCircle, MdPublic } from 'react-icons/md';
import { useModalRouter } from '../../../ModalRouter_context';
import { TRANSLATION_LANGUAGES } from '../MediaTranslationsManagerModal';

interface SortableMediaThumbnailProps {
  media: NonNullable<ImagesSection_MainQuery['ad']>['medias'][number];
  index: number;
  onRemove: (index: number) => void;
  onMediaClick: (media: NonNullable<ImagesSection_MainQuery['ad']>['medias'][number]) => void;
  isDragging: boolean;
  isSelecting?: boolean;
  selectedRole?: MediaRole | null;
  selectedMedias?: NonNullable<ImagesSection_MainQuery['ad']>['medias'];
}

function RolePill({ role }: { role: MediaRole }) {
  const bgColor = role === MediaRole.Main ? 'bg-blue-500' : 'bg-purple-500';
  
  return (
    <span className={`${bgColor} text-white text-xs px-2 py-0.5 rounded-full`}>
      {role.toLowerCase()}
    </span>
  );
}

type MediaTranslationSummary = {
  lang: string;
  langMediaActive: boolean;
};

function translationCounts(media: SortableMediaThumbnailProps['media']) {
  const translations = (media as typeof media & {
    translations?: MediaTranslationSummary[] | null;
  }).translations ?? [];
  const activeLangs = new Set(
    translations
      .filter((translation) => translation.langMediaActive)
      .map((translation) => translation.lang),
  );
  const existingCount = TRANSLATION_LANGUAGES.filter((lang) => activeLangs.has(lang)).length;
  return {
    existingCount,
    missingCount: TRANSLATION_LANGUAGES.length - existingCount,
  };
}

export function SortableMediaThumbnail({
  media,
  index,
  onRemove,
  onMediaClick,
  isDragging,
  isSelecting = false,
  selectedRole,
  selectedMedias = [],
}: SortableMediaThumbnailProps) {
  const modalRouter = useModalRouter();
  const isSelected = selectedMedias.some(selectedMedia => selectedMedia.id === media.id);
  const { existingCount, missingCount } = translationCounts(media);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isSortableDragging ? 1 : 0,
    opacity: isSortableDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={isDragging ? 'cursor-grabbing' : 'cursor-grab'}
    >
      <div 
        className={`relative group aspect-square rounded-lg ${
          isSelected ? 'ring-4 ring-blue-500' : ''
        } ${
          (isSelecting || selectedRole || selectedMedias.length > 0) ? 'cursor-pointer' : 'cursor-zoom-in'
        }`}
        onClick={() => onMediaClick(media)}
      >
        {/* Media roles pills */}
        <div className="absolute top-2 left-2 flex gap-1 z-10">
          {media.roles.map((role) => (
            <RolePill key={role} role={role} />
          ))}
        </div>

        {media.type === MediaType.Video ? (
          <div className="relative w-full h-full">
            <video
              src={media.url}
              className="w-full h-full object-cover rounded-lg"
              muted
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <MdPlayCircle 
                size={48} 
                className="text-white opacity-75 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        ) : (
          <img
            src={media.url}
            alt={`Media ${index + 1}`}
            className="w-full h-full object-contain border-2 border-gray-300 rounded-lg"
          />
        )}

        {media.type === MediaType.Image && (
          <button
            type="button"
            className="absolute bottom-2 left-2 z-10 px-1.5 py-0.5 rounded-md border border-gray-300 bg-white flex items-center gap-1 whitespace-nowrap leading-none cursor-pointer text-sky-400 opacity-70 transition-all duration-150 group-hover:opacity-100"
            aria-label={`${existingCount} existing, ${missingCount} missing`}
            title={`${existingCount} existing / ${missingCount} missing`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              modalRouter.push({
                s: 'mediaTranslation',
                a: { mediaId: media.id },
              });
            }}
          >
            <MdPublic className="w-3.5 h-3.5 shrink-0" />
            <span className="text-xs font-medium text-gray-600">
              {existingCount}/{missingCount}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <MdClose size={16} />
        </button>
      </div>
    </div>
  );
} 